<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useTranslation } from "i18next-vue";
import { FlashMessageInfoStatus } from "@/types/enums";

import type { Profile, ProfileNewData } from "@/types/profile";
import type { ChangeCallbackParams } from "@/types/config";
import type { ErrorTexts } from "@/types/flashMessages";

import { useComponentErrorCapture, getErrorMessage } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";

import { profilesStore } from "@/stores/profiles";
import { addInfoFull, addErrorFull, addErrorBottom } from "@/stores/messages";

import Modal from "@/components/elements/Modal.vue";
import Dialog from "@/components/elements/Dialog.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import UCInput from "@/components/ui/UCInput.vue";
import UCToggle from "@/components/ui/UCToggle.vue";
import ErrorBox from "@/components/ui/ErrorBox.vue";

const { t } = useTranslation();
const { sleep } = useTiming();

useComponentErrorCapture();

defineExpose({
  open
});
const emit = defineEmits(["close"]);

const profileSkeleton = {
  name: "",
  icon: null,
  restricted: false,
}

const dialogActiveProfile = ref();
const dialogDelete = ref();
const profilesStorage = profilesStore();
const profileId = ref<string | null>(null);
const profile = ref<Profile | null>();
const showModal = ref(false);
const errorMessage = ref("");
const deleting = ref(false);
const allProfiles = ref<Profile[]>([]);
const activeProfiles = ref<Profile[]>([]);
const errorProfile = ref<ErrorTexts | null>(null);

watch(showModal, async (val) => {
  if (val) {
    if (profileId.value == null) {
      profile.value = JSON.parse(JSON.stringify(profileSkeleton));
    } else {
      try {
        allProfiles.value = await profilesStorage.getAll();
        activeProfiles.value = await profilesStorage.getAllActive();
        setProfile(allProfiles.value);
      } catch (e: any) {
        addErrorBottom(e);
      }
    }
  }
})

profilesStorage.$subscribe((mutation, state) => {
  if (state.profiles && showModal.value == true) {
    setProfile(state.profiles);
    activeProfiles.value = state.activeProfiles;
    allProfiles.value = state.profiles;
  }
})

const isActiveProfile = computed(() => {
  return activeProfiles.value.some(p => p.profile_id === profileId.value);
})

const newProfile = computed(() => {
  return profileId.value == null;
})

const questionDeleteProfile = computed(() => {
  return t('profile.delete_profile.question', { name: profile.value ? profile.value.name : '' });
})

function open(id: string | null = null) {
  profileId.value = id;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  profileId.value = null;
  profile.value = null;
}

async function setProfile(allProfiles: Profile[]) {
  const profileToEdit = allProfiles.find(p => p.profile_id == profileId.value)
  if (profileToEdit && profileToEdit != null) {
    profile.value = JSON.parse(JSON.stringify(profileToEdit));
  }
}

async function getProfiles() {
  try {
    await profilesStorage.getAll(true);
    await profilesStorage.getAllActive(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function addProfile() {
  if (profile.value?.name == "") {
    errorMessage.value = t("ui.required_field");
    return false;
  }
  const profileData: ProfileNewData = {
    name: profile.value?.name || "",
    icon: profile.value?.icon,
    restricted: profile.value?.restricted,
  };

  try {
    addInfoFull(FlashMessageInfoStatus.SAVING, t("profile.message.adding_profile"));
    await profilesStorage.create(profileData);
    addInfoFull(FlashMessageInfoStatus.SUCCESS, t("profile.message.profile_added"));
    await sleep(2000);
    closeModal();
  } catch (e: any) {
    addErrorFull(e, 'profile.add');
  }
  getProfiles();
}

async function doSaveProfile(changes: any) {
  errorProfile.value = null;
  if (!profile.value || profile.value == null) {
    return false;
  }

  try {
    await profilesStorage.update(profile.value?.profile_id, changes);
  } catch (e: any) {
    errorProfile.value = getErrorMessage(e, 'profile.update');
  }
  await getProfiles();
}

async function deleteProfile() {
  deleting.value = true;
  try {
    addInfoFull(FlashMessageInfoStatus.SAVING, t("profile.message.deleting_profile"));
    await profilesStorage.delete(profile.value as Profile);
    await getProfiles();
    addInfoFull(FlashMessageInfoStatus.SUCCESS, t("profile.message.profile_deleted"));
    await sleep(2000);
    closeModal();
  } catch (e: any) {
    addErrorFull(e, 'profile.delete');
  }
  deleting.value = false;
}

function startDelete() {
  if (isActiveProfile.value) {
    openActiveProfileDialog();
  } else {
    openDeleteDialog();
  }
}

function openActiveProfileDialog() {
  dialogActiveProfile.value.open();
}

function openDeleteDialog() {
  dialogDelete.value.open();
}

async function changeProfileIcon(params: ChangeCallbackParams) {
  const { value } = params;
  if (newProfile.value) {
    if (profile.value) {
      profile.value.icon = value as string;
    }
  } else {
    await doSaveProfile({
      update_pin: false,
      icon: value as string,
    });
  }
}

async function changeName() {
  if (newProfile.value || profile.value?.name == null || typeof profile.value?.name == 'undefined') {
    return false;
  }

  await doSaveProfile({
    update_pin: false,
    name: profile.value?.name as string,
  });
}

async function changeRestricted() {
  if (newProfile.value || profile.value?.restricted == null || typeof profile.value?.restricted == 'undefined') {
    return false;
  }

  await doSaveProfile({
    update_pin: false,
    restricted: profile.value?.restricted as boolean,
  });
}

function clearErrors() {
  if (errorMessage.value.length > 0) {
    errorMessage.value = '';
  }
}

</script>
<template>
  <Teleport to="body">
    <Modal
      :show="showModal"
      :width="'25rem'"
      :button-back="true"
      @close="closeModal"
      name="edit-profile"
      class="modal--edit-profile">
      <template #header>
        <template v-if="newProfile">{{ $t("profile.add_profile") }}</template>
        <template v-else>{{ $t("profile.edit_profile") }}</template>
      </template>
      <template v-if="profile != null">
        <ErrorBox v-if="errorProfile" :message="errorProfile" :marginBottom="true" />
        <IconSelect
          :key="profile.icon ? profile.icon : 'fa-regular fa-user'"
          :value="profile.icon ? profile.icon : 'fa-regular fa-user'"
          :change-callback="changeProfileIcon"
          :circle="true"
          :title="$t('profile.select_an_icon')"
        />
        <UCInput
          v-model="profile.name"
          @submit="changeName"
          @click="clearErrors"
          :label="$t('profile.name')"
          :error-message="errorMessage ? $t(errorMessage) : ''"
          :full-w="true"
          :focus="newProfile"
        />
        <UCToggle
          v-model="profile.restricted"
          @change="changeRestricted"
          :label="$t('profile.restricted_profile')"
          :full-w="true"
          :description="$t('profile.restricted_profile_description')"
        />
      </template>
      <template #footer>
        <button v-if="newProfile" @click="addProfile" class="button button--primary button--min-w-grow">
          {{ $t("profile.add_profile") }}
        </button>
        <button v-else @click="startDelete" class="button button--danger button--min-w-grow">
          {{ $t("ui.delete") }}
        </button>
      </template>
    </Modal>
  </Teleport>
  <Dialog
    ref="dialogActiveProfile"
    :title="$t('profile.delete_already_active.title')"
    :text="$t('profile.delete_already_active.info')"
    :submit-text="$t('ui.dismiss')"
    :text-center="true"
  />
  <Dialog
    ref="dialogDelete"
    @submit="deleteProfile"
    :title="$t('profile.delete_profile.title')"
    :text="questionDeleteProfile"
    :submit-text="$t('ui.delete')"
    :cancel-text="$t('ui.cancel')"
    :disable-buttons="deleting"
  />
</template>